import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { API_BASE, parseJsonResponse } from "../api";
import { FaPlus, FaEdit, FaTrash, FaCheck, FaClock, FaSignInAlt, FaSignOutAlt, FaBlog, FaStar, FaArrowLeft, FaTimes, FaImage } from "react-icons/fa";
import "../css/styles.css";

const Admin = () => {
  const token = localStorage.getItem("nyatwende_token");
  const user = JSON.parse(localStorage.getItem("nyatwende_user") || "null");
  const isAdmin = user && user.role === "admin";

  const [tab, setTab] = useState("blogs");
  const [authError, setAuthError] = useState("");

  // Blog state
  const [blogs, setBlogs] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: "", slug: "", description: "", content: "", image: "", author: "", route: "" });
  const [editingBlogId, setEditingBlogId] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogLoading, setBlogLoading] = useState(false);
  const [blogMsg, setBlogMsg] = useState("");
  const fileInputRef = useRef(null);

  // Review state
  const [reviews, setReviews] = useState([]);
  const [reviewMsg, setReviewMsg] = useState("");

  const headers = { "Content-Type": "application/json", "Authorization": `Bearer ${token}` };

  // Fetch data
  useEffect(() => {
    if (!isAdmin) return;
    fetchBlogs();
    fetchReviews();
  }, [isAdmin]);

  const fetchBlogs = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/blogs/all`, { headers });
      if (res.status === 401) { setAuthError("Session expired. Please sign out and sign in again."); return; }
      if (!res.ok) return;
      const data = await res.json();
      setBlogs(Array.isArray(data) ? data : []);
    } catch { }
  };

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/reviews/all`, { headers });
      if (res.status === 401) { setAuthError("Session expired. Please sign out and sign in again."); return; }
      if (!res.ok) return;
      const data = await res.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch { }
  };

  // Blog CRUD
  const resetBlogForm = () => {
    setBlogForm({ title: "", slug: "", description: "", content: "", image: "", author: "", route: "" });
    setEditingBlogId(null);
    setShowBlogForm(false);
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    setBlogLoading(true);
    setBlogMsg("");
    try {
      const url = editingBlogId ? `${API_BASE}/api/blogs/${editingBlogId}` : `${API_BASE}/api/blogs`;
      const method = editingBlogId ? "PUT" : "POST";
      const res = await fetch(url, { method, headers, body: JSON.stringify(blogForm) });
      const data = await parseJsonResponse(res);
      if (!res.ok) throw new Error(data.error || "Failed");
      setBlogMsg(data.message);
      resetBlogForm();
      fetchBlogs();
    } catch (err) {
      setBlogMsg("Error: " + err.message);
    } finally {
      setBlogLoading(false);
    }
  };

  const handleEditBlog = (blog) => {
    setBlogForm({
      title: blog.title || "",
      slug: blog.slug || "",
      description: blog.description || "",
      content: blog.content || "",
      image: blog.image || "",
      author: blog.author || "",
      route: blog.route || "",
    });
    setEditingBlogId(blog.id);
    setShowBlogForm(true);
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try {
      await fetch(`${API_BASE}/api/blogs/${id}`, { method: "DELETE", headers });
      fetchBlogs();
      setBlogMsg("Blog deleted");
    } catch {
      setBlogMsg("Failed to delete");
    }
  };

  const handleToggleFeatured = async (blog) => {
    try {
      await fetch(`${API_BASE}/api/blogs/${blog.id}`, {
        method: "PUT", headers,
        body: JSON.stringify({ is_featured: !blog.is_featured }),
      });
      fetchBlogs();
    } catch { }
  };

  const handleApproveBlog = async (id) => {
    try {
      await fetch(`${API_BASE}/api/blogs/${id}/approve`, { method: "PUT", headers });
      fetchBlogs();
      setBlogMsg("Blog approved");
    } catch {
      setBlogMsg("Failed to approve");
    }
  };

  // Image upload via Cloudinary (unsigned not needed - we pass URL)
  const handleImageUrl = (e) => {
    setBlogForm({ ...blogForm, image: e.target.value });
  };

  // Review actions
  const handleApproveReview = async (id) => {
    try {
      await fetch(`${API_BASE}/api/reviews/${id}/approve`, { method: "PUT", headers });
      fetchReviews();
      setReviewMsg("Review approved");
    } catch {
      setReviewMsg("Failed to approve");
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await fetch(`${API_BASE}/api/reviews/${id}`, { method: "DELETE", headers });
      fetchReviews();
      setReviewMsg("Review deleted");
    } catch {
      setReviewMsg("Failed to delete");
    }
  };

  // Not logged in or not admin
  if (!token || !user) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Nyatwende Admin</h1>
          <p>Please sign in with Google using the button in the navigation bar.</p>
          <Link to="/" className="admin-btn primary">Go to Home</Link>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Access Denied</h1>
          <p>Your account ({user.email}) does not have admin access.</p>
          <Link to="/blogs" className="admin-btn primary">Go to Blogs</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Nyatwende Admin</h1>
        <span style={{ color: "#888", fontSize: "0.9rem" }}>{user.name} ({user.email})</span>
      </div>

      {authError && (
        <div className="admin-msg" style={{ background: "rgba(220,53,69,0.15)", border: "1px solid rgba(220,53,69,0.4)", color: "#dc3545", padding: "14px 20px", borderRadius: "10px", margin: "16px 0", textAlign: "center" }}>
          {authError}
          <br />
          <button style={{ marginTop: "0.5rem", padding: "0.4rem 1rem", borderRadius: "6px", border: "none", background: "#dc3545", color: "#fff", cursor: "pointer" }} onClick={() => { localStorage.removeItem("nyatwende_token"); localStorage.removeItem("nyatwende_user"); window.location.reload(); }}>
            Sign Out & Re-login
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={tab === "blogs" ? "active" : ""} onClick={() => setTab("blogs")}>
          <FaBlog /> Blogs ({blogs.length})
        </button>
        <button className={tab === "reviews" ? "active" : ""} onClick={() => setTab("reviews")}>
          <FaStar /> Reviews ({reviews.length})
        </button>
      </div>

      {/* BLOGS TAB */}
      {tab === "blogs" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Blog Posts</h2>
            <span className="admin-badge">{blogs.filter(b => !b.approved).length} pending</span>
            <button className="admin-btn primary" onClick={() => { resetBlogForm(); setShowBlogForm(true); }}>
              <FaPlus /> New Blog
            </button>
          </div>

          {blogMsg && <div className="admin-msg">{blogMsg}</div>}

          {/* Blog Form */}
          {showBlogForm && (
            <div className="admin-form-card">
              <div className="admin-form-header">
                <h3>{editingBlogId ? "Edit Blog" : "Create New Blog"}</h3>
                <button className="admin-btn-icon" onClick={resetBlogForm}><FaTimes /></button>
              </div>
              <form onSubmit={handleBlogSubmit} className="admin-form">
                <div className="admin-form-group">
                  <label>Title *</label>
                  <input type="text" value={blogForm.title} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} required />
                </div>
                <div className="admin-form-row">
                  <div className="admin-form-group">
                    <label>Slug</label>
                    <input type="text" value={blogForm.slug} onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })} placeholder="auto-generated from title" />
                  </div>
                  <div className="admin-form-group">
                    <label>Author</label>
                    <input type="text" value={blogForm.author} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} placeholder="Nyatwende Team" />
                  </div>
                </div>
                <div className="admin-form-group">
                  <label>Description *</label>
                  <textarea rows="2" value={blogForm.description} onChange={(e) => setBlogForm({ ...blogForm, description: e.target.value })} required />
                </div>
                <div className="admin-form-group">
                  <label>Content (HTML)</label>
                  <textarea rows="8" value={blogForm.content} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} placeholder="Full blog content..." />
                </div>
                <div className="admin-form-group">
                  <label><FaImage /> Image URL</label>
                  <input type="text" value={blogForm.image} onChange={handleImageUrl} placeholder="https://..." />
                  {blogForm.image && <img src={blogForm.image} alt="Preview" className="admin-img-preview" />}
                </div>
                <div className="admin-form-group">
                  <label>Custom Route (optional)</label>
                  <input type="text" value={blogForm.route} onChange={(e) => setBlogForm({ ...blogForm, route: e.target.value })} placeholder="/blogs/my-custom-page" />
                </div>
                <div className="admin-form-actions">
                  <button type="submit" className="admin-btn primary" disabled={blogLoading}>
                    {blogLoading ? "Saving..." : editingBlogId ? "Update Blog" : "Create Blog"}
                  </button>
                  <button type="button" className="admin-btn" onClick={resetBlogForm}>Cancel</button>
                </div>
              </form>
            </div>
          )}

          {/* Blog List */}
          <div className="admin-table">
            {blogs.map(blog => (
              <div key={blog.id} className={`admin-card ${!blog.approved ? "pending" : ""}`}>
                <div className="admin-card-img">
                  {blog.image ? <img src={blog.image} alt={blog.title} /> : <div className="admin-card-noimg"><FaImage /></div>}
                </div>
                <div className="admin-card-body">
                  <h4>
                    {blog.title}
                    {!blog.approved && <span className="admin-badge pending"><FaClock /> Pending</span>}
                    {blog.approved && blog.is_featured && <span className="admin-badge featured">Featured</span>}
                  </h4>
                  <p className="admin-card-meta">
                    {blog.author_name || blog.author} &middot; {new Date(blog.created_at).toLocaleDateString()}
                  </p>
                  <p className="admin-card-desc">{blog.description}</p>
                </div>
                <div className="admin-card-actions">
                  {!blog.approved && (
                    <button title="Approve" onClick={() => handleApproveBlog(blog.id)} className="admin-btn-icon approve"><FaCheck /></button>
                  )}
                  <button title="Toggle featured" onClick={() => handleToggleFeatured(blog)} className={`admin-btn-icon ${blog.is_featured ? "active" : ""}`}>
                    <FaStar />
                  </button>
                  <button title="Edit" onClick={() => handleEditBlog(blog)} className="admin-btn-icon"><FaEdit /></button>
                  <button title="Delete" onClick={() => handleDeleteBlog(blog.id)} className="admin-btn-icon danger"><FaTrash /></button>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <p className="admin-empty">No blog posts yet.</p>}
          </div>
        </div>
      )}

      {/* REVIEWS TAB */}
      {tab === "reviews" && (
        <div className="admin-section">
          <div className="admin-section-header">
            <h2>Reviews</h2>
            <span className="admin-badge">{reviews.filter(r => !r.approved).length} pending</span>
          </div>

          {reviewMsg && <div className="admin-msg">{reviewMsg}</div>}

          <div className="admin-table">
            {reviews.map(review => (
              <div key={review.id} className={`admin-card ${!review.approved ? "pending" : ""}`}>
                <div className="admin-card-body">
                  <h4>
                    {review.name}
                    {!review.approved && <span className="admin-badge pending"><FaClock /> Pending</span>}
                    {review.approved && <span className="admin-badge approved"><FaCheck /> Approved</span>}
                  </h4>
                  <p className="admin-card-meta">
                    {review.location} &middot; {review.safari} &middot;
                    {"★".repeat(review.rating)}{"☆".repeat(5 - review.rating)} &middot;
                    {new Date(review.created_at).toLocaleDateString()}
                  </p>
                  <p className="admin-card-desc">"{review.text}"</p>
                </div>
                <div className="admin-card-actions">
                  {!review.approved && (
                    <button title="Approve" onClick={() => handleApproveReview(review.id)} className="admin-btn-icon approve"><FaCheck /></button>
                  )}
                  <button title="Delete" onClick={() => handleDeleteReview(review.id)} className="admin-btn-icon danger"><FaTrash /></button>
                </div>
              </div>
            ))}
            {reviews.length === 0 && <p className="admin-empty">No reviews yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
